import React, { useState, useCallback } from 'react';
import DOMPurify from 'dompurify';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Type,
  Image,
  MousePointer,
  Minus,
  Square,
  Columns,
  GripVertical,
  Trash2,
  Copy,
  Smartphone,
  Monitor,
  Eye,
  X,
  Plus,
  Palette,
  Link,
  AlignLeft,
  AlignCenter,
  AlignRight,
} from 'lucide-react';

// Configure DOMPurify for email-safe HTML
const sanitizeHTML = (html) => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 
                   'ul', 'ol', 'li', 'a', 'img', 'div', 'span', 'table', 'tr', 'td', 'th',
                   'thead', 'tbody', 'blockquote', 'pre', 'code'],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'style', 'class', 'width', 'height', 'target'],
    ALLOW_DATA_ATTR: false,
  });
};

// Block type definitions
const BLOCK_TYPES = {
  TEXT: 'text',
  IMAGE: 'image',
  BUTTON: 'button',
  DIVIDER: 'divider',
  SPACER: 'spacer',
  COLUMNS: 'columns',
};

// Available blocks in sidebar
const AVAILABLE_BLOCKS = [
  { type: BLOCK_TYPES.TEXT, icon: Type, label: 'Text' },
  { type: BLOCK_TYPES.IMAGE, icon: Image, label: 'Image' },
  { type: BLOCK_TYPES.BUTTON, icon: MousePointer, label: 'Button' },
  { type: BLOCK_TYPES.DIVIDER, icon: Minus, label: 'Divider' },
  { type: BLOCK_TYPES.SPACER, icon: Square, label: 'Spacer' },
  { type: BLOCK_TYPES.COLUMNS, icon: Columns, label: '2 Columns' },
];

// Default block content
const getDefaultBlockContent = (type) => {
  switch (type) {
    case BLOCK_TYPES.TEXT:
      return {
        html: '<p>Click to edit this text. You can make it <strong>bold</strong>, <em>italic</em>, or add <a href="#">links</a>.</p>',
        alignment: 'left',
        padding: 16,
      };
    case BLOCK_TYPES.IMAGE:
      return {
        src: '',
        alt: 'Image',
        width: '100%',
        alignment: 'center',
        link: '',
        padding: 16,
      };
    case BLOCK_TYPES.BUTTON:
      return {
        text: 'Click Here',
        link: '#',
        backgroundColor: '#E14A1B',
        textColor: '#ffffff',
        alignment: 'center',
        borderRadius: 8,
        padding: 16,
      };
    case BLOCK_TYPES.DIVIDER:
      return {
        color: '#e5e5e5',
        thickness: 1,
        width: '100%',
        padding: 16,
      };
    case BLOCK_TYPES.SPACER:
      return {
        height: 32,
      };
    case BLOCK_TYPES.COLUMNS:
      return {
        columns: [
          { html: '<p>Left column content</p>' },
          { html: '<p>Right column content</p>' },
        ],
        gap: 16,
        padding: 16,
      };
    default:
      return {};
  }
};

// Generate unique ID
const generateId = () => `block_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Draggable block from sidebar
const DraggableBlock = ({ type, icon: Icon, label }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
    id: `sidebar_${type}`,
    data: { type, fromSidebar: true },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="flex items-center gap-3 p-3 bg-background border rounded-lg cursor-grab hover:border-primary hover:bg-primary/5 transition-colors touch-none"
    >
      <Icon className="w-5 h-5 text-muted-foreground" />
      <span className="text-sm font-medium">{label}</span>
    </div>
  );
};

// Sortable block in canvas
const SortableBlock = ({ block, isSelected, onSelect, onDelete, onDuplicate, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative ${isSelected ? 'ring-2 ring-primary' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(block.id);
      }}
    >
      {/* Block controls */}
      <div className={`absolute -left-10 top-1/2 -translate-y-1/2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity ${isSelected ? 'opacity-100' : ''}`}>
        <button
          {...attributes}
          {...listeners}
          className="p-1 rounded bg-muted hover:bg-muted/80 cursor-grab touch-none"
        >
          <GripVertical className="w-4 h-4" />
        </button>
      </div>
      <div className={`absolute -right-10 top-1/2 -translate-y-1/2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity ${isSelected ? 'opacity-100' : ''}`}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDuplicate(block.id);
          }}
          className="p-1 rounded bg-muted hover:bg-muted/80"
        >
          <Copy className="w-4 h-4" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(block.id);
          }}
          className="p-1 rounded bg-destructive/10 hover:bg-destructive/20 text-destructive"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      {children}
    </div>
  );
};

// Block renderers
const TextBlockRenderer = ({ content, isEditing, onChange }) => {
  if (isEditing) {
    return (
      <div style={{ padding: content.padding, textAlign: content.alignment }}>
        <Textarea
          value={content.html.replace(/<[^>]*>/g, '')}
          onChange={(e) => onChange({ ...content, html: `<p>${e.target.value}</p>` })}
          className="min-h-[100px] w-full"
          placeholder="Enter your text..."
        />
      </div>
    );
  }
  return (
    <div
      style={{ padding: content.padding, textAlign: content.alignment }}
      dangerouslySetInnerHTML={{ __html: sanitizeHTML(content.html) }}
      className="prose prose-sm max-w-none"
    />
  );
};

const ImageBlockRenderer = ({ content }) => {
  return (
    <div style={{ padding: content.padding, textAlign: content.alignment }}>
      {content.src ? (
        <img
          src={content.src}
          alt={content.alt}
          style={{ width: content.width, maxWidth: '100%' }}
          className="inline-block"
        />
      ) : (
        <div className="w-full h-40 bg-muted rounded-lg flex items-center justify-center border-2 border-dashed">
          <div className="text-center text-muted-foreground">
            <Image className="w-8 h-8 mx-auto mb-2" />
            <p className="text-sm">Add image URL in properties</p>
          </div>
        </div>
      )}
    </div>
  );
};

const ButtonBlockRenderer = ({ content }) => {
  return (
    <div style={{ padding: content.padding, textAlign: content.alignment }}>
      <a
        href={content.link}
        style={{
          backgroundColor: content.backgroundColor,
          color: content.textColor,
          borderRadius: content.borderRadius,
          display: 'inline-block',
          padding: '12px 24px',
          textDecoration: 'none',
          fontWeight: 600,
        }}
        onClick={(e) => e.preventDefault()}
      >
        {content.text}
      </a>
    </div>
  );
};

const DividerBlockRenderer = ({ content }) => {
  return (
    <div style={{ padding: content.padding }}>
      <hr
        style={{
          border: 'none',
          borderTop: `${content.thickness}px solid ${content.color}`,
          width: content.width,
          margin: '0 auto',
        }}
      />
    </div>
  );
};

const SpacerBlockRenderer = ({ content }) => {
  return <div style={{ height: content.height }} />;
};

const ColumnsBlockRenderer = ({ content }) => {
  return (
    <div style={{ padding: content.padding }}>
      <div style={{ display: 'flex', gap: content.gap }}>
        {content.columns.map((col, idx) => (
          <div
            key={idx}
            style={{ flex: 1 }}
            dangerouslySetInnerHTML={{ __html: sanitizeHTML(col.html) }}
            className="prose prose-sm max-w-none"
          />
        ))}
      </div>
    </div>
  );
};

// Block renderer switch
const BlockRenderer = ({ block, isSelected, onChange }) => {
  switch (block.type) {
    case BLOCK_TYPES.TEXT:
      return <TextBlockRenderer content={block.content} isEditing={isSelected} onChange={onChange} />;
    case BLOCK_TYPES.IMAGE:
      return <ImageBlockRenderer content={block.content} />;
    case BLOCK_TYPES.BUTTON:
      return <ButtonBlockRenderer content={block.content} />;
    case BLOCK_TYPES.DIVIDER:
      return <DividerBlockRenderer content={block.content} />;
    case BLOCK_TYPES.SPACER:
      return <SpacerBlockRenderer content={block.content} />;
    case BLOCK_TYPES.COLUMNS:
      return <ColumnsBlockRenderer content={block.content} />;
    default:
      return null;
  }
};

// Properties panel for selected block
const PropertiesPanel = ({ block, onChange, brandingColors }) => {
  if (!block) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <p className="text-sm">Select a block to edit its properties</p>
      </div>
    );
  }

  const updateContent = (updates) => {
    onChange({ ...block, content: { ...block.content, ...updates } });
  };

  return (
    <div className="p-4 space-y-4">
      <h3 className="font-semibold text-sm uppercase text-muted-foreground">
        {block.type} Properties
      </h3>

      {/* Text block properties */}
      {block.type === BLOCK_TYPES.TEXT && (
        <>
          <div className="space-y-2">
            <Label>Content</Label>
            <Textarea
              value={block.content.html.replace(/<[^>]*>/g, '')}
              onChange={(e) => updateContent({ html: `<p>${e.target.value}</p>` })}
              rows={4}
            />
          </div>
          <div className="space-y-2">
            <Label>Alignment</Label>
            <div className="flex gap-2">
              {['left', 'center', 'right'].map((align) => (
                <Button
                  key={align}
                  variant={block.content.alignment === align ? 'default' : 'outline'}
                  size="icon"
                  onClick={() => updateContent({ alignment: align })}
                >
                  {align === 'left' && <AlignLeft className="w-4 h-4" />}
                  {align === 'center' && <AlignCenter className="w-4 h-4" />}
                  {align === 'right' && <AlignRight className="w-4 h-4" />}
                </Button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Image block properties */}
      {block.type === BLOCK_TYPES.IMAGE && (
        <>
          <div className="space-y-2">
            <Label>Image URL</Label>
            <Input
              value={block.content.src}
              onChange={(e) => updateContent({ src: e.target.value })}
              placeholder="https://..."
            />
          </div>
          <div className="space-y-2">
            <Label>Alt Text</Label>
            <Input
              value={block.content.alt}
              onChange={(e) => updateContent({ alt: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label>Link (optional)</Label>
            <Input
              value={block.content.link}
              onChange={(e) => updateContent({ link: e.target.value })}
              placeholder="https://..."
            />
          </div>
          <div className="space-y-2">
            <Label>Alignment</Label>
            <div className="flex gap-2">
              {['left', 'center', 'right'].map((align) => (
                <Button
                  key={align}
                  variant={block.content.alignment === align ? 'default' : 'outline'}
                  size="icon"
                  onClick={() => updateContent({ alignment: align })}
                >
                  {align === 'left' && <AlignLeft className="w-4 h-4" />}
                  {align === 'center' && <AlignCenter className="w-4 h-4" />}
                  {align === 'right' && <AlignRight className="w-4 h-4" />}
                </Button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Button block properties */}
      {block.type === BLOCK_TYPES.BUTTON && (
        <>
          <div className="space-y-2">
            <Label>Button Text</Label>
            <Input
              value={block.content.text}
              onChange={(e) => updateContent({ text: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label>Link URL</Label>
            <Input
              value={block.content.link}
              onChange={(e) => updateContent({ link: e.target.value })}
              placeholder="https://..."
            />
          </div>
          <div className="space-y-2">
            <Label>Background Color</Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={block.content.backgroundColor}
                onChange={(e) => updateContent({ backgroundColor: e.target.value })}
                className="w-12 h-10 p-1 cursor-pointer"
              />
              <Input
                value={block.content.backgroundColor}
                onChange={(e) => updateContent({ backgroundColor: e.target.value })}
                className="flex-1"
              />
            </div>
            {brandingColors && (
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => updateContent({ backgroundColor: brandingColors.primary })}
                  className="w-6 h-6 rounded border"
                  style={{ backgroundColor: brandingColors.primary }}
                  title="Primary color"
                />
                <button
                  onClick={() => updateContent({ backgroundColor: brandingColors.secondary })}
                  className="w-6 h-6 rounded border"
                  style={{ backgroundColor: brandingColors.secondary }}
                  title="Secondary color"
                />
                <button
                  onClick={() => updateContent({ backgroundColor: brandingColors.accent })}
                  className="w-6 h-6 rounded border"
                  style={{ backgroundColor: brandingColors.accent }}
                  title="Accent color"
                />
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label>Text Color</Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={block.content.textColor}
                onChange={(e) => updateContent({ textColor: e.target.value })}
                className="w-12 h-10 p-1 cursor-pointer"
              />
              <Input
                value={block.content.textColor}
                onChange={(e) => updateContent({ textColor: e.target.value })}
                className="flex-1"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Alignment</Label>
            <div className="flex gap-2">
              {['left', 'center', 'right'].map((align) => (
                <Button
                  key={align}
                  variant={block.content.alignment === align ? 'default' : 'outline'}
                  size="icon"
                  onClick={() => updateContent({ alignment: align })}
                >
                  {align === 'left' && <AlignLeft className="w-4 h-4" />}
                  {align === 'center' && <AlignCenter className="w-4 h-4" />}
                  {align === 'right' && <AlignRight className="w-4 h-4" />}
                </Button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Divider block properties */}
      {block.type === BLOCK_TYPES.DIVIDER && (
        <>
          <div className="space-y-2">
            <Label>Color</Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={block.content.color}
                onChange={(e) => updateContent({ color: e.target.value })}
                className="w-12 h-10 p-1 cursor-pointer"
              />
              <Input
                value={block.content.color}
                onChange={(e) => updateContent({ color: e.target.value })}
                className="flex-1"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Thickness (px)</Label>
            <Input
              type="number"
              value={block.content.thickness}
              onChange={(e) => updateContent({ thickness: parseInt(e.target.value) || 1 })}
              min={1}
              max={10}
            />
          </div>
        </>
      )}

      {/* Spacer block properties */}
      {block.type === BLOCK_TYPES.SPACER && (
        <div className="space-y-2">
          <Label>Height (px)</Label>
          <Input
            type="number"
            value={block.content.height}
            onChange={(e) => updateContent({ height: parseInt(e.target.value) || 16 })}
            min={8}
            max={200}
          />
        </div>
      )}

      {/* Columns block properties */}
      {block.type === BLOCK_TYPES.COLUMNS && (
        <>
          <div className="space-y-2">
            <Label>Left Column</Label>
            <Textarea
              value={block.content.columns[0]?.html.replace(/<[^>]*>/g, '') || ''}
              onChange={(e) => {
                const newColumns = [...block.content.columns];
                newColumns[0] = { html: `<p>${e.target.value}</p>` };
                updateContent({ columns: newColumns });
              }}
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label>Right Column</Label>
            <Textarea
              value={block.content.columns[1]?.html.replace(/<[^>]*>/g, '') || ''}
              onChange={(e) => {
                const newColumns = [...block.content.columns];
                newColumns[1] = { html: `<p>${e.target.value}</p>` };
                updateContent({ columns: newColumns });
              }}
              rows={3}
            />
          </div>
        </>
      )}

      {/* Common padding control */}
      {block.content.padding !== undefined && (
        <div className="space-y-2">
          <Label>Padding (px)</Label>
          <Input
            type="number"
            value={block.content.padding}
            onChange={(e) => updateContent({ padding: parseInt(e.target.value) || 0 })}
            min={0}
            max={100}
          />
        </div>
      )}
    </div>
  );
};

// Main Email Builder Component
const EmailBuilder = ({ initialBlocks = [], onChange, brandingColors, onSave }) => {
  const [blocks, setBlocks] = useState(initialBlocks);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [previewMode, setPreviewMode] = useState(null); // null, 'desktop', 'mobile'

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const selectedBlock = blocks.find((b) => b.id === selectedBlockId);

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    // Adding new block from sidebar
    if (active.data.current?.fromSidebar) {
      const newBlock = {
        id: generateId(),
        type: active.data.current.type,
        content: getDefaultBlockContent(active.data.current.type),
      };

      const overIndex = blocks.findIndex((b) => b.id === over.id);
      if (overIndex >= 0) {
        const newBlocks = [...blocks];
        newBlocks.splice(overIndex, 0, newBlock);
        setBlocks(newBlocks);
      } else {
        setBlocks([...blocks, newBlock]);
      }
      setSelectedBlockId(newBlock.id);
      return;
    }

    // Reordering existing blocks
    if (active.id !== over.id) {
      setBlocks((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleBlockChange = (updatedBlock) => {
    setBlocks((prev) =>
      prev.map((b) => (b.id === updatedBlock.id ? updatedBlock : b))
    );
  };

  const handleDeleteBlock = (blockId) => {
    setBlocks((prev) => prev.filter((b) => b.id !== blockId));
    if (selectedBlockId === blockId) {
      setSelectedBlockId(null);
    }
  };

  const handleDuplicateBlock = (blockId) => {
    const block = blocks.find((b) => b.id === blockId);
    if (block) {
      const newBlock = {
        ...block,
        id: generateId(),
        content: { ...block.content },
      };
      const index = blocks.findIndex((b) => b.id === blockId);
      const newBlocks = [...blocks];
      newBlocks.splice(index + 1, 0, newBlock);
      setBlocks(newBlocks);
      setSelectedBlockId(newBlock.id);
    }
  };

  const addBlock = (type) => {
    const newBlock = {
      id: generateId(),
      type,
      content: getDefaultBlockContent(type),
    };
    setBlocks([...blocks, newBlock]);
    setSelectedBlockId(newBlock.id);
  };

  // Notify parent of changes
  React.useEffect(() => {
    onChange?.(blocks);
  }, [blocks, onChange]);

  // Render preview modal inline instead of as nested component
  const renderPreviewModal = () => {
    if (!previewMode) return null;

    const width = previewMode === 'mobile' ? '375px' : '600px';

    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-background rounded-xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center gap-2">
              {previewMode === 'mobile' ? (
                <Smartphone className="w-5 h-5" />
              ) : (
                <Monitor className="w-5 h-5" />
              )}
              <span className="font-medium">
                {previewMode === 'mobile' ? 'Mobile' : 'Desktop'} Preview
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={previewMode === 'desktop' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPreviewMode('desktop')}
              >
                <Monitor className="w-4 h-4" />
              </Button>
              <Button
                variant={previewMode === 'mobile' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPreviewMode('mobile')}
              >
                <Smartphone className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => setPreviewMode(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="overflow-auto p-4 bg-muted flex-1">
            <div
              style={{ width, margin: '0 auto' }}
              className="bg-white shadow-lg rounded-lg overflow-hidden"
            >
              {blocks.map((block) => (
                <BlockRenderer key={block.id} block={block} isSelected={false} />
              ))}
              {blocks.length === 0 && (
                <div className="p-12 text-center text-muted-foreground">
                  No content yet
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-full">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        {/* Sidebar - Block palette */}
        <div className="w-56 border-r bg-muted/30 p-4 flex flex-col">
          <h3 className="font-semibold text-sm uppercase text-muted-foreground mb-4">
            Blocks
          </h3>
          <div className="space-y-2 flex-1">
            {AVAILABLE_BLOCKS.map((block) => (
              <div
                key={block.type}
                onClick={() => addBlock(block.type)}
                className="flex items-center gap-3 p-3 bg-background border rounded-lg cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
              >
                <block.icon className="w-5 h-5 text-muted-foreground" />
                <span className="text-sm font-medium">{block.label}</span>
                <Plus className="w-4 h-4 ml-auto text-muted-foreground" />
              </div>
            ))}
          </div>
          
          <div className="border-t pt-4 mt-4 space-y-2">
            <Button
              variant="outline"
              className="w-full gap-2"
              onClick={() => setPreviewMode('desktop')}
            >
              <Eye className="w-4 h-4" />
              Preview
            </Button>
            {onSave && (
              <Button className="w-full" onClick={() => onSave(blocks)}>
                Use This Design
              </Button>
            )}
          </div>
        </div>

        {/* Canvas - Email editor */}
        <div
          className="flex-1 overflow-auto p-8 bg-muted/50"
          onClick={() => setSelectedBlockId(null)}
        >
          <div className="max-w-[600px] mx-auto bg-background shadow-lg rounded-lg min-h-[400px]">
            <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
              {blocks.map((block) => (
                <SortableBlock
                  key={block.id}
                  block={block}
                  isSelected={selectedBlockId === block.id}
                  onSelect={setSelectedBlockId}
                  onDelete={handleDeleteBlock}
                  onDuplicate={handleDuplicateBlock}
                >
                  <BlockRenderer
                    block={block}
                    isSelected={selectedBlockId === block.id}
                    onChange={(content) => handleBlockChange({ ...block, content })}
                  />
                </SortableBlock>
              ))}
            </SortableContext>

            {blocks.length === 0 && (
              <div className="p-12 text-center text-muted-foreground border-2 border-dashed rounded-lg m-4">
                <Palette className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="font-medium mb-2">Start building your email</p>
                <p className="text-sm">Click a block on the left to add it here</p>
              </div>
            )}
          </div>
        </div>

        {/* Properties panel */}
        <div className="w-72 border-l bg-muted/30 overflow-auto">
          <div className="p-4 border-b">
            <h3 className="font-semibold">Properties</h3>
          </div>
          <PropertiesPanel
            block={selectedBlock}
            onChange={handleBlockChange}
            brandingColors={brandingColors}
          />
        </div>

        <DragOverlay>
          {activeId && activeId.toString().startsWith('sidebar_') && (
            <div className="p-3 bg-primary text-primary-foreground rounded-lg shadow-lg">
              {AVAILABLE_BLOCKS.find((b) => `sidebar_${b.type}` === activeId)?.label}
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {renderPreviewModal()}
    </div>
  );
};

export default EmailBuilder;
